import { Dialog, Transition } from '@headlessui/react'
import { Fragment, ReactNode } from 'react'
import { MODAL_TRANSITION_CLASSES, type ModalTransitionVariant } from '@/constants/modal'

interface ModalLayoutProps {
	showModal: boolean
	toggleModal: () => void
	panelClassName?: string
	children: ReactNode
	placement?: string
	isStatic?: boolean
	transitionVariant?: ModalTransitionVariant
}

/** Headless UI dialog shell — opaque backdrop, high z-index above data grids. */
const ModalLayout = ({
	showModal,
	toggleModal,
	panelClassName,
	children,
	placement,
	isStatic,
	transitionVariant = 'default',
}: ModalLayoutProps) => {
	const panelTransition = MODAL_TRANSITION_CLASSES[transitionVariant]

	return (
		<Transition appear show={showModal} as={Fragment}>
			<Dialog
				static={isStatic ?? false}
				as="div"
				className="relative z-[1200]"
				onClose={isStatic ? () => null : toggleModal}
			>
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm dark:bg-black/75" aria-hidden="true" />
				</Transition.Child>

				<div className="fixed inset-0 overflow-y-auto">
					<div className={`flex min-h-full w-full ${placement ?? 'justify-center items-center p-4 sm:p-6'}`}>
						<Transition.Child
							as={Fragment}
							enter={panelTransition.enter}
							enterFrom={panelTransition.enterFrom}
							enterTo={panelTransition.enterTo}
							leave={panelTransition.leave}
							leaveFrom={panelTransition.leaveFrom}
							leaveTo={panelTransition.leaveTo}
						>
							<Dialog.Panel
								className={`w-full transform text-left align-middle transition-all duration-300 ease-out-expo ${panelClassName ?? ''}`}
							>
								{children}
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition>
	)
}

export default ModalLayout
